import { useState, type FormEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Google as GoogleIcon } from "@mui/icons-material";
import type { SsupAuthActions } from "./useSsupAuthActions";

/**
 * The signed-out sign-in screen. A segmented control chooses the audience —
 * "Client" (partner: email/password) is the default; "Admin" (StoryWorm staff:
 * Google) is the alternative. Purely presentational over {@link SsupAuthActions}.
 */
type Mode = "admin" | "client";

export function SignInPanel({
  actions,
  onOpenDocs,
}: {
  actions: SsupAuthActions;
  onOpenDocs: () => void;
}) {
  const { busy, error, clearError, signInWithPassword, signInWithGoogle } = actions;
  const [mode, setMode] = useState<Mode>("client");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const selectMode = (_event: unknown, next: Mode | null) => {
    if (next) {
      setMode(next);
      clearError();
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void signInWithPassword(email, password);
  };

  return (
    <Paper sx={{ p: 4 }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h2">Partner Validation Portal</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Sign in to validate your ingested universe.
          </Typography>
        </Box>

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          color="primary"
          value={mode}
          onChange={selectMode}
          aria-label="Account type"
        >
          <ToggleButton value="admin">Admin</ToggleButton>
          <ToggleButton value="client">Client</ToggleButton>
        </ToggleButtonGroup>

        {error && <Alert severity="error">{error}</Alert>}

        {mode === "admin" ? (
          <Button
            variant="contained"
            size="large"
            startIcon={<GoogleIcon />}
            disabled={busy}
            onClick={() => void signInWithGoogle()}
          >
            {busy ? "Please wait…" : "Continue with Google"}
          </Button>
        ) : (
          <Box component="form" onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
              />
              <Button type="submit" variant="contained" size="large" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </Stack>
          </Box>
        )}

        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={onOpenDocs}
          sx={{ alignSelf: "center" }}
        >
          New here? Read the partner guide
        </Link>
      </Stack>
    </Paper>
  );
}
